import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Key, Eye, EyeOff, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChangeMyPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });

  const username = localStorage.getItem('username') || 'User';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.current_password || !form.new_password || !form.confirm_password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (form.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error('New password and confirm password do not match');
      return;
    }
    if (form.current_password === form.new_password) {
      toast.error('New password must be different from current password');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: form.current_password, new_password: form.new_password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed successfully. Please login again with your new password.');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
      // Force re-login for security
      setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        localStorage.removeItem('role');
        navigate('/login');
      }, 1500);
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const PwField = ({ id, label, value, onChange, visible, toggle }) => (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          data-testid={`change-pwd-${id}`}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={label}
          autoComplete="new-password"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={toggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          data-testid={`change-pwd-${id}-toggle`}
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-indigo-50 py-8" data-testid="change-my-password-page">
      <div className="max-w-xl mx-auto px-6">
        <div className="flex items-center space-x-4 mb-8">
          <Button variant="outline" onClick={() => navigate(-1)} data-testid="change-pwd-back">
            <ArrowLeft className="mr-2" size={18} />
            Back
          </Button>
          <h1 className="text-3xl font-bold" style={{ color: '#4f46e5' }}>
            Change My Password
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border p-6 space-y-5">
          <div className="flex items-center space-x-3 pb-2 border-b">
            <div className="p-2 rounded-full bg-indigo-100">
              <Key className="text-indigo-600" size={20} />
            </div>
            <div>
              <p className="font-semibold text-gray-800">Logged in as: {username}</p>
              <p className="text-xs text-gray-500">You'll be logged out after a successful change.</p>
            </div>
          </div>

          <PwField
            id="current"
            label="Current Password"
            value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            visible={show.current}
            toggle={() => setShow({ ...show, current: !show.current })}
          />
          <PwField
            id="new"
            label="New Password (min 6 chars)"
            value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            visible={show.next}
            toggle={() => setShow({ ...show, next: !show.next })}
          />
          <PwField
            id="confirm"
            label="Confirm New Password"
            value={form.confirm_password}
            onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
            visible={show.confirm}
            toggle={() => setShow({ ...show, confirm: !show.confirm })}
          />

          <Button
            type="submit"
            disabled={loading}
            className="w-full"
            style={{ backgroundColor: '#4f46e5', color: 'white' }}
            data-testid="change-pwd-submit"
          >
            <Save className="mr-2" size={18} />
            {loading ? 'Changing...' : 'Change Password'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ChangeMyPassword;
